// =====================================================================
// src/lib/governanca.js — o que a tela de governança DECIDE, sem DOM
//
// A regra do estado do quarto NÃO está aqui: mora em v_quadro_governanca
// (0008/0009). Aqui só o que é da tela: agrupar por andar, contar, ordenar
// a fila, montar a lista da camareira a partir de `na_minha_lista`, ler a
// ficha, formatar hora e cronômetro. Tudo puro, testado em node
// (scripts/teste_tela.mjs).
// =====================================================================

// 0086 (decisão 2B do Hugo, 29/10/2026): `aguarda_checkout` — o quarto sai
// hoje (ou já devia ter saído) e o hóspede continua dentro. Aparece na lista
// da camareira, cinza, e não deixa começar; fica fora dos contadores.
export const ESTADOS = ['a_limpar', 'em_limpeza', 'aguardando_inspecao', 'liberado', 'aguarda_checkout', 'sem_movimento'];

export const ROTULO_ESTADO = {
  a_limpar: 'A limpar',
  em_limpeza: 'Em limpeza',
  aguardando_inspecao: 'Aguardando inspeção',
  liberado: 'Liberado',
  aguarda_checkout: 'Aguarda check-out',
  sem_movimento: 'Sem movimento',
};

// tipo sugerido pelo quadro → como a camareira lê (e a cor da pílula)
export const TIPOS = {
  troca:     { rotulo: 'Troca',     icone: 'ic-refresh', classe: 'pill-troca' },
  saida:     { rotulo: 'Saída',     icone: 'ic-door',    classe: 'pill-saida' },
  entrada:   { rotulo: 'Entrada',   icone: 'ic-bell',    classe: 'pill-entrada' },
  arrumacao: { rotulo: 'Arrumação', icone: 'ic-broom',   classe: 'pill-arrumacao' },
  avulsa:    { rotulo: 'Avulsa',    icone: 'ic-broom',   classe: 'pill-avulsa' },
};

// O tipo que uma limpeza nova recebe: o que o quadro sugere; num quarto sem
// movimento, avulsa.
export function tipoParaIniciar(quarto) {
  return quarto.tipo_sugerido || 'avulsa';
}

// 0086 (2B): o recado do toque num quarto que ainda não pode ser limpo.
export function avisoAguardaCheckout(q) {
  return `Quarto ${q.numero} aguarda o check-out da recepção. A limpeza de saída começa depois dele.`;
}

// [{andar, quartos:[...]}] em ordem de andar; quartos sem andar por último,
// sob "Sem andar".
export function agruparPorAndar(linhas) {
  const mapa = new Map();
  for (const q of linhas) {
    const k = q.andar === null || q.andar === undefined ? null : Number(q.andar);
    if (!mapa.has(k)) mapa.set(k, []);
    mapa.get(k).push(q);
  }
  const chaves = [...mapa.keys()].sort((a, b) => (a === null) - (b === null) || a - b);
  return chaves.map(andar => ({
    andar,
    rotulo: andar === null ? 'Sem andar' : `${andar}º andar`,
    quartos: mapa.get(andar).slice().sort(ordemDeQuarto),
  }));
}

export function ordemDeQuarto(a, b) {
  return String(a.numero).localeCompare(String(b.numero), 'pt-BR', { numeric: true });
}

// O que a camareira vê: só o que está na lista dela (a view decide) e só o
// que tem o que fazer — sem movimento fica fora, salvo se ela mesma abriu
// uma limpeza avulsa nele (aí é dela até terminar).
export function listaDaCamareira(linhas) {
  return linhas.filter(q => q.na_minha_lista && (q.estado !== 'sem_movimento' || q.limpeza_id));
}

// Os vagos sem movimento dos andares dela: podem receber limpeza avulsa.
// 0069 (Hugo, 28/09): o quarto bloqueado fica fora — "para limpar,
// primeiro desbloqueia" (o banco recusa abrir limpeza nele).
export function vagosDaCamareira(linhas) {
  return linhas.filter(q => q.na_minha_lista && q.estado === 'sem_movimento' && !q.limpeza_id && !q.bloqueio).sort(ordemDeQuarto);
}

// Os quatro contadores da supervisão (sem movimento não conta: é o que não há para fazer).
export function contadores(linhas) {
  const c = { a_limpar: 0, em_limpeza: 0, aguardando_inspecao: 0, liberado: 0 };
  for (const q of linhas) if (q.estado in c) c[q.estado]++;
  return c;
}

// Resumo da lista (as pílulas do topo): quantas trocas, saídas, entradas, arrumações.
export function resumoDeTipos(linhas) {
  const r = { troca: 0, saida: 0, entrada: 0, arrumacao: 0 };
  for (const q of linhas) if (q.tipo_sugerido in r) r[q.tipo_sugerido]++;
  return r;
}

// A fila de inspeção: terminados sem inspeção, quem terminou primeiro na frente.
export function filaDeInspecao(linhas) {
  return linhas.filter(q => q.estado === 'aguardando_inspecao')
    .sort((a, b) => String(a.terminada_em).localeCompare(String(b.terminada_em)));
}

// O quadro do dia (linhas da supervisão): tudo que não é sem movimento,
// vermelho primeiro, depois amarelo, azul, verde; dentro, por número.
// Dentro do vermelho, quem CHEGA CEDO (0021: o hóspede avisou no
// pré-check-in que chega antes da hora de check-in) vem primeiro — é a
// prioridade da limpeza.
const ORDEM_QUADRO = { a_limpar: 0, em_limpeza: 1, aguardando_inspecao: 2, liberado: 3, aguarda_checkout: 4 };
export function quadroDoDia(linhas) {
  return linhas.filter(q => q.estado !== 'sem_movimento')
    .sort((a, b) => (ORDEM_QUADRO[a.estado] - ORDEM_QUADRO[b.estado]) || (Number(!!b.chega_cedo) - Number(!!a.chega_cedo)) || ordemDeQuarto(a, b));
}

// "chega antes das 12h" — o aviso da chegada cedo, para a linha do quarto.
export const ROTULO_FAIXA = Object.freeze({ antes_12: 'antes das 12h', '12_15': 'entre 12h e 15h', '15_18': 'entre 15h e 18h', depois_18: 'depois das 18h' });
export function avisoChegada(q) {
  if (!q || !q.chega_hoje || !q.chegada_prevista) return '';
  return (q.chega_cedo ? 'chega CEDO, ' : 'chega ') + (ROTULO_FAIXA[q.chegada_prevista] || q.chegada_prevista);
}

// A situação de um quarto, em uma linha, para a supervisão.
export function situacaoDoQuarto(q, agora) {
  switch (q.estado) {
    case 'em_limpeza':          return `${q.camareira_nome} · desde ${hora(q.iniciada_em)} · ${duracao(q.iniciada_em, agora)}`;
    case 'aguardando_inspecao': return `${q.camareira_nome} · terminou às ${hora(q.terminada_em)}`;
    case 'liberado':            return `liberado por ${q.inspecionada_por_nome} às ${hora(q.inspecionada_em)}`;
    case 'a_limpar':            return q.inspecao_resultado === 'devolvida'
                                  ? `devolvido por ${q.inspecionada_por_nome}: ${q.inspecao_obs || ''}`
                                  : 'ninguém entrou ainda';
    case 'aguarda_checkout':    return 'hóspede ainda no quarto — limpa depois do check-out';
    default:                    return '';
  }
}

// ── a ficha ──────────────────────────────────────────────────────────
// checklist é um jsonb {chave: valor}; os itens vêm de checklist_itens.
// marcar → true/false; quantidade → número. Feito = true ou número > 0.
// Os quatro blocos da ficha, com a cor e o ícone que o Monteiro de Moraes
// usa em `secaoChecklist` (index.html, 15794). A cor não é enfeite: é como a
// camareira acha o bloco de manutenção no meio da ficha com a mão ocupada.
// Grupo que o hotel criar e não estiver aqui cai no padrão azul.
export const GRUPOS_DA_FICHA = {
  limpeza:    { rotulo: 'Limpeza e Arrumação',  cor: '#1B3A7A', icone: 'ic-broom' },
  amenidades: { rotulo: 'Amenidades / Higiene', cor: '#059669', icone: 'ic-amenity' },
  frigobar:   { rotulo: 'Frigobar',             cor: '#7c3aed', icone: 'ic-frigobar' },
  manutencao: { rotulo: 'Manutenção',           cor: '#dc2626', icone: 'ic-wrench' },
};
export function grupoDaFicha(chave) {
  return GRUPOS_DA_FICHA[chave] || { rotulo: chave, cor: '#1B3A7A', icone: 'ic-clipboard' };
}

// Um bloco da ficha pode depender de MÓDULO CONTRATADO, e não só de existir
// item semeado. `frigobar` é o primeiro caso, decidido pelo Hugo em
// 17/09/2026: a função fica construída, o bloco fica escondido, e só quem
// tiver o módulo (hoje a Monteiro de Moraes) enxerga — no futuro ele vira
// add-on pago para os demais. A chave é a mesma do catálogo `public.modulos`
// (0002), então quem manda é `contratos_modulo`, não uma lista nova.
export const GRUPO_EXIGE_MODULO = { frigobar: 'frigobar' };

// Os itens da ficha que ESTA pessoa deve ver, tirando os de bloco que exige
// módulo não contratado. Filtrar aqui, na entrada, e não só na hora de
// desenhar, porque o mesmo conjunto alimenta o PROGRESSO: esconder o bloco e
// continuar contando os itens dele daria uma ficha 15/17 que nunca fecha.
// Padrão fail-closed, pelo mesmo motivo de `gruposDaFicha`.
export function itensDaFicha(itens, { modulos = [] } = {}) {
  const tem = chave => modulos.some(m => m && m.chave === chave);
  return (itens || []).filter(i => {
    const exigido = GRUPO_EXIGE_MODULO[i.grupo];
    return !exigido || tem(exigido);
  });
}

// Um item `ok_solicito` vale 'ok' ou 'solicito'; sem valor, não foi checado.
// Só 'ok' conta como feito — é a conta do MdM (`manutDone`), e é a certa: um
// "Solicito" é trabalho que ainda vai acontecer, não item resolvido.
export function itemFeito(valor) {
  return valor === true || valor === 'ok' || (typeof valor === 'number' && valor > 0);
}
export function itemSolicitado(valor) { return valor === 'solicito'; }

export function progressoDaFicha(checklist, itens) {
  const c = checklist || {};
  const ativos = itens.filter(i => i.ativo !== false);
  const feitos = ativos.filter(i => itemFeito(c[i.chave])).length;
  return { feitos, total: ativos.length };
}

// Agrupa os itens da ficha por `grupo`, na ordem em que aparecem (ordem, rótulo).
// `modulos` é a lista de `contexto.modulos` (só vem o que a pessoa pode ver:
// `meu_contexto` filtra por `app.nivel in ('ver','editar')`, 0012). O padrão é
// lista VAZIA de propósito — quem esquecer de passar perde o bloco que exige
// módulo, em vez de mostrá-lo a quem não contratou. Esconder por engano é
// conserto de uma linha; mostrar por engano é o hotel vendo o que não comprou.
export function gruposDaFicha(itens, { modulos = [] } = {}) {
  const ordenados = itensDaFicha(itens, { modulos }).filter(i => i.ativo !== false)
    .slice().sort((a, b) => (a.ordem - b.ordem) || a.rotulo.localeCompare(b.rotulo, 'pt-BR'));
  const grupos = [];
  for (const i of ordenados) {
    let g = grupos.find(x => x.grupo === i.grupo);
    if (!g) { g = { grupo: i.grupo, itens: [] }; grupos.push(g); }
    g.itens.push(i);
  }
  return grupos;   // o corte por módulo já aconteceu em `itensDaFicha`
}

// Um novo checklist com o item alterado (não muda o original).
export function marcarItem(checklist, item, valor) {
  const novo = { ...(checklist || {}) };
  if (item.tipo === 'quantidade') {
    const n = Number(valor);
    novo[item.chave] = Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  } else if (item.tipo === 'ok_solicito') {
    // Tocar de novo no botão que já está aceso desmarca. É o que a camareira
    // espera quando errou o toque — e desmarcar tem de ser de graça, porque o
    // chamado só nasce ao TERMINAR a ficha (terminar_limpeza, 0029).
    novo[item.chave] = (valor === 'ok' || valor === 'solicito')
      ? (novo[item.chave] === valor ? null : valor)
      : null;
    if (novo[item.chave] === null) delete novo[item.chave];
  } else {
    novo[item.chave] = !!valor;
  }
  return novo;
}

// Quais itens de manutenção estão marcados como "Solicito" AGORA — é o que a
// ficha mostra em vermelho antes de terminar, e o que virará chamado.
export function solicitacoesDaFicha(checklist, itens) {
  const c = checklist || {};
  return itens.filter(i => i.ativo !== false && i.tipo === 'ok_solicito' && itemSolicitado(c[i.chave]));
}

// ── tempo ────────────────────────────────────────────────────────────
export function hora(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

// "mm:ss" ou "h:mm:ss" entre um instante ISO e agora (ms).
export function duracao(inicioIso, agoraMs, fimIso) {
  if (!inicioIso) return '00:00';
  const ini = new Date(inicioIso).getTime();
  const fim = fimIso ? new Date(fimIso).getTime() : agoraMs;
  let s = Math.max(0, Math.floor((fim - ini) / 1000));
  const h = Math.floor(s / 3600); s -= h * 3600;
  const m = Math.floor(s / 60); s -= m * 60;
  const mm = String(m).padStart(2, '0'), ss = String(s).padStart(2, '0');
  return h ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

export function dataPorExtenso(d) {
  return d.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' });
}

// O aviso de quem segue escala e não tem andar hoje (decisão: vê NADA, com aviso).
export function avisoDeEscala(ctx) {
  if (!ctx || !ctx.segue_escala) return null;
  if (ctx.volante) return null;
  const andares = ctx.andares_hoje || [];
  if (andares.length) return null;
  return 'Você não está escalada para nenhum andar hoje. Fale com a supervisão.';
}

// Para a volante: os andares com mais vermelho primeiro (é para lá que ela
// vai). Empate: mais quartos com o que fazer; depois o número do andar.
export function ordenarPorNecessidade(grupos) {
  const nota = g => ({
    vermelhos: g.quartos.filter(q => q.estado === 'a_limpar').length,
    pendentes: g.quartos.filter(q => q.estado === 'a_limpar' || q.estado === 'em_limpeza').length,
  });
  return grupos.slice().sort((a, b) => {
    const na = nota(a), nb = nota(b);
    return (nb.vermelhos - na.vermelhos) || (nb.pendentes - na.pendentes) || ((a.andar === null) - (b.andar === null)) || (a.andar - b.andar);
  });
}

// "2º andar" · "4º andar (401–420)" — o que a camareira tem hoje, como ela lê.
export function minhaEscalaPorExtenso(ctx) {
  if (!ctx) return '';
  if (ctx.volante) return 'volante — o hotel inteiro, onde houver mais necessidade';
  const f = ctx.faixas_hoje || [];
  if (f.length) return f.map(x => `${x.andar}º andar${x.de ? ` (${x.de}–${x.ate})` : ''}`).join(', ');
  return andaresPorExtenso(ctx.andares_hoje);
}

export function andaresPorExtenso(andares) {
  const a = (andares || []).map(n => `${n}º`);
  if (!a.length) return '';
  if (a.length === 1) return a[0] + ' andar';
  return a.slice(0, -1).join(', ') + ' e ' + a[a.length - 1] + ' andares';
}
